import { useEffect, useState, useRef } from "react";
import { observer, inject } from "mobx-react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import pwdImg from "../assets/img/lock.svg";
import userImg from "../assets/img/user.svg";
import { Text } from "../UI";
import Button from "../UI/Button";
import { useInput, inputsHasError } from "../utils/hooks";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  isNotEmpty,
  withError,
  isValidEmailAddress,
  isStrongPassword,
} from "../validation";
import { ClipLoader } from "react-spinners";
import InputMUI from "../UI/TextInput/InputMui";
import { tenantsStore } from "../stores";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useMobile } from "../utils/mobile";
import backImg from "../assets/img/back_img.png";
import MessageNotification, {
  useMessageNotification,
  MessageType,
} from "../components/MessageNotification";
import { CircleDiv } from "../components/Common";

const ScreenType = {
  LOGIN_ADMIN: "login_admin",
  LOGIN_TENANT: "login_tenant",
  NEW_PASSWORD_ADMIN: "new_password_admin",
  FORGOT_PASSWORD_ADMIN: "forgot_password_admin",
  CREATE_PASSWORD_ADMIN: "create_password_admin",
  ENTER_PASSWORD_ADMIN: "enter_password_admin",
};

const Label = styled("div")(({ theme }) => ({
  fontSize: 12,
  color: theme.color.label,
  fontWeight: 600,
  letterSpacing: 0.32,
  textAlign: "start",
  lineHeight: 3,
}));

const LanguageMenu = styled("div")(({ theme, isRtl }) => ({
  position: "absolute",
  transform: "translateY(15px)",
  overflow: "auto",
  backgroundColor: "#fff",
  padding: 10,
  color: theme.color.text,
  zIndex: 99,
  boxShadow: `0px 4px 20px #C3C0D3`,
  borderRadius: 18,
  top: 48,
  left: isRtl ? 0 : "unset",
  right: isRtl ? "unset" : 0,
  width: 190,
}));

const LanguageMenuItem = styled("div")(({ theme, selected }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  cursor: "pointer",
  height: 40,
  backgroundColor: selected ? theme.color.background : "unset",
  ":hover": {
    borderRadius: 20,
    backgroundColor: theme.color.gray10,
  },
}));

const LoginAdmin = ({ userStore, type }) => {
  const [searchParams] = useSearchParams();
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [dialCode, setDialCode] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [adminPasswordChecked, setAdminPasswordChecked] = useState(false);
  const [adminHasPassword, setAdminHasPassword] = useState(false);

  const { messageNotification, setMessageNotification } =
    useMessageNotification();

  const theme = useTheme();
  const { t, i18n } = useTranslation("login");
  const navigate = useNavigate();
  const isMobile = useMobile();
  const isRtl = i18n.dir() === "rtl";
  const decryptUser = searchParams.get("u");

  const submitButtonRef = useRef(null);
  const langMenuRef = useRef(null);
  const [showLangMenu, setShowLangMenu] = useState(false);

  const getButtonPosition = (buttonRef) => {
    if (!buttonRef?.current) return null;
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
  };

  const showNotification = (text, type, buttonRef) => {
    const buttonPosition = buttonRef ? getButtonPosition(buttonRef) : null;
    setMessageNotification({
      text,
      show: true,
      type,
      buttonPosition,
    });
  };

  const isNotEmptyWithError = withError(
    isNotEmpty,
    t("text_field_empty_error"),
  );
  const isValidEmailAddressWithError = withError(
    isValidEmailAddress,
    t("text_invalid_email_address_error"),
  );

  const isNotEqual = (input) => {
    const inputString = String(input);
    return inputString === currentUser.pwd;
  };

  const isNotEqualWithError = withError(isNotEqual, t("text_not_equal_error"));

  const isNotStrongPasswordWithError = withError(
    isStrongPassword,
    t("text_field_not_strong_password_error"),
  );

  function useStickyState(defaultValue, key, prop) {
    const [value, setValue] = useState(() => {
      const stickyValue = window.localStorage.getItem(key);
      return stickyValue !== null
        ? JSON.parse(stickyValue)[prop]
        : defaultValue;
    });

    return [value, setValue];
  }

  const [currentUser, setUser] = useStickyState(
    {
      email: userStore?.userDetails?.email,
      pwd: userStore?.userDetails?.pwd,
    },
    "coralis_tenants-user",
    "userDetails",
  );

  const handleChange = (prop) => (event) => {
    setUser({ ...currentUser, [prop]: event.target.value });
  };

  useEffect(() => {
    let savedLanguage = window.localStorage.getItem(
      "coralis-language-preference",
    );

    if (!savedLanguage) {
      try {
        const sessionStorageUser = window.sessionStorage.getItem(
          "coralis-tenants-user",
        );
        if (sessionStorageUser) {
          const parsed = JSON.parse(sessionStorageUser);
          savedLanguage = parsed?.language;
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }

    savedLanguage = savedLanguage || userStore.language || "he";

    i18n.changeLanguage(savedLanguage);
    if (userStore.language !== savedLanguage) {
      userStore.setLanguage(savedLanguage);
    }
    window.localStorage.setItem("coralis-language-preference", savedLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setShowLangMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const changeLanguageHandler = (lang) => {
    i18n.changeLanguage(lang);
    userStore.setLanguage(lang);
    window.localStorage.setItem("coralis-language-preference", lang);
    setShowLangMenu(false);
  };

  useEffect(() => {
    if (!!userStore.error) {
      const validationErrorKeys = [
        "text_field_not_strong_password_error",
        "text_field_empty_error",
        "text_invalid_email_address_error",
        "text_not_equal_error",
      ];

      const containsValidationKey = validationErrorKeys.some((key) =>
        userStore.error.includes(key),
      );

      const translatedValidationErrors = validationErrorKeys.map((key) =>
        t(key),
      );
      const matchesTranslatedError = translatedValidationErrors.some(
        (translatedError) =>
          userStore.error === translatedError ||
          userStore.error.includes(translatedError),
      );

      const isValidationError = containsValidationKey || matchesTranslatedError;

      if (!isValidationError) {
        showNotification(
          t(userStore.error),
          MessageType.ERROR,
          submitButtonRef,
        );
      } else {
        setMessageNotification({
          show: false,
        });
      }
    } else {
      setMessageNotification({
        show: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore.error, t]);

  const email = useInput({
    validation: [isNotEmptyWithError, isValidEmailAddressWithError],
    placeholder: t("email_placeholder"),
    value: currentUser.email || "",
  });

  const pwd = useInput({
    placeholder:
      type === ScreenType.LOGIN_ADMIN || type === ScreenType.LOGIN_TENANT
        ? t("pwd_placeholder")
        : t("new_pwd_placeholder"),
    validation: [isNotEmptyWithError, isNotStrongPasswordWithError],
    value: currentUser.pwd || "",
    label:
      type === ScreenType.LOGIN_ADMIN || type === ScreenType.LOGIN_TENANT
        ? t("pwd")
        : t("new_pwd"),
  });

  const newPwd = useInput({
    placeholder: t("confirmation_pwd_placeholder"),
    validation: [
      isNotEmptyWithError,
      isNotEqualWithError,
      isNotStrongPasswordWithError,
    ],
    value: currentUser.newPwd || "",
    label: t("confirmation_pwd"),
  });

  const getTitle = (type) => {
    switch (type) {
      case ScreenType.LOGIN_ADMIN:
      case ScreenType.LOGIN_TENANT:
        return t("title");
      case ScreenType.NEW_PASSWORD_ADMIN:
        return t("new_password_text");
      case ScreenType.FORGOT_PASSWORD_ADMIN:
        return t("reset_pwd");
      default:
        return "";
    }
  };
  const getSubTitle = (type) => {
    switch (type) {
      case ScreenType.LOGIN_ADMIN:
        return t("login_text_admin");
      case ScreenType.LOGIN_TENANT:
        return t("login_text_tenant");
      case ScreenType.NEW_PASSWORD_ADMIN:
        return t("new_password_text");
      case ScreenType.FORGOT_PASSWORD_ADMIN:
        return t("reset_pwd_text");
      default:
        return "";
    }
  };

  const validatePhone = (phonenum) => {
    return !!phonenum;
  };

  const handleSubmit = async () => {
    if (!!validatePhone(phone)) {
      let rawPhone = phone.slice(dialCode.length);
      if (countryCode === "il") {
        rawPhone = rawPhone.padStart(10, "0");
      }
      const data = {
        CELLPHONE: rawPhone,
      };

      setLoading(true);
      const res = await userStore.loginTenant(data);
      setLoading(false);
      if (res.isSucceed) {
        navigate(`/tenants/opt`);
      }
    }
  };

  const handleAdminSubmit = async () => {
    if (!adminPasswordChecked && !inputsHasError(email)) {
      const checkPasswordData = {
        USERNAME: currentUser.email,
      };

      setLoading(true);
      const passwordCheckResult =
        await userStore.checkAdminPassword(checkPasswordData);
      setLoading(false);

      if (!passwordCheckResult.isSucceed) {
        showNotification(
          t(
            passwordCheckResult.error ||
            "error:error_failed_check_password_req",
          ),
          MessageType.ERROR,
          submitButtonRef,
        );
        return;
      }

      setAdminPasswordChecked(true);
      setAdminHasPassword(passwordCheckResult.hasPassword);
      userStore.setUserDetails({
        HAS_PASSWORD: passwordCheckResult.hasPassword,
        USERNAME: currentUser.email,
      });
      return;
    }

    if (adminPasswordChecked) {
      if (!adminHasPassword) {
        if (inputsHasError(pwd, newPwd)) {
          return;
        }
        const data = {
          USERNAME: currentUser.email,
          WEBPASSWORD: currentUser.newPwd,
        };
        setLoading(true);
        const res = await tenantsStore.setAdminPassword(data);
        setLoading(false);

        if (res.isSucceed) {
          navigate("/admin/opt", { replace: false });
        }
      } else {
        if (inputsHasError(email, pwd)) {
          return;
        }
        const data = {
          USERNAME: currentUser.email,
          WEBPASSWORD: currentUser.pwd,
        };
        setLoading(true);
        const res = await userStore.verifyAdminPassword(data);
        setLoading(false);

        if (res.isSucceed) {
          navigate("/admin/opt", { replace: false });
        }
      }
    }
  };

  const handleSend = async () => {
    if (!inputsHasError(email)) {
      const data = {
        USERNAME: currentUser.email,
        body_subject: t("pwd_subject"),
        href: `${window.location.origin}/new_password_admin`,
      };
      setLoading(true);

      const userResponse = await tenantsStore.sendResetPassword(data);
      setLoading(false);
      if (userResponse.isSucceed) {
        navigate(`/admin/login/`);
      }
    }
  };

  const handleResetPassword = async () => {
    if (inputsHasError(pwd, newPwd)) {
      return;
    }
    const data = {
      DECRYPT_USERNAME: decryptUser,
      WEBPASSWORD: currentUser.newPwd,
      MAINCUST: null,
      PASSCHGREQFLAG: "N",
    };
    setLoading(true);
    const res = await tenantsStore.resetPassword(data);
    setLoading(false);

    if (res.isSucceed) {
      navigate(`/admin/login/`);
    }
  };
  const handelByType = async (e, type) => {
    e.preventDefault();
    switch (type) {
      case ScreenType.LOGIN_TENANT: {
        handleSubmit();
        break;
      }
      case ScreenType.LOGIN_ADMIN: {
        handleAdminSubmit();
        break;
      }
      case ScreenType.FORGOT_PASSWORD_ADMIN: {
        handleSend();
        break;
      }
      case ScreenType.NEW_PASSWORD_ADMIN: {
        handleResetPassword();
        break;
      }
      default:
        return "";
    }
  };

  const handleOnChange = (value, data) => {
    setPhone(value);
    setCountryCode(data.countryCode);
    setDialCode(data.dialCode);
  };

  return (
    <form
      onSubmit={(e) => handelByType(e, type)}
      style={{
        background: `url(${backImg}) no-repeat left center fixed`,
      }}
    >
      <div
        style={{
          position: "fixed",
          top: 16,
          right: isRtl ? "unset" : 30,
          left: isRtl ? 30 : "unset",
          display: "flex",
          flexDirection: "column",
          zIndex: 100,
        }}
      >
        <CircleDiv
          style={{
            border: `1px solid ${theme.color.gray30}`,
            textTransform: "capitalize",
            cursor: "pointer",
          }}
          backgroundColor={theme.color.white}
          onClick={() => setShowLangMenu(!showLangMenu)}
        >
          <Text.SM
            color={theme.color.gray90}
            style={{
              textAlign: "center",
            }}
          >
            {t(userStore.language || "he")}
          </Text.SM>
        </CircleDiv>
        {showLangMenu && (
          <LanguageMenu
            isRtl={isRtl}
            ref={langMenuRef}
            style={{ textTransform: "capitalize" }}
          >
            <LanguageMenuItem
              onClick={() => changeLanguageHandler("he")}
              style={{ marginBlockEnd: 12 }}
            >
              <CircleDiv
                backgroundColor={theme.color.gray30}
                height={35}
                width={35}
                style={{ marginInlineEnd: 10 }}
              >
                <Text.BM
                  size={16}
                  color={theme.color.gray90}
                  style={{
                    textAlign: "center",
                  }}
                >
                  {t("he")}
                </Text.BM>
              </CircleDiv>
              {t("hebrew")}
            </LanguageMenuItem>
            <LanguageMenuItem onClick={() => changeLanguageHandler("en")}>
              <CircleDiv
                height={35}
                width={35}
                backgroundColor={theme.color.gray30}
                style={{ marginInlineEnd: 10 }}
              >
                <Text.BM
                  size={16}
                  color={theme.color.gray90}
                  style={{
                    textAlign: "center",
                  }}
                >
                  {t("en")}
                </Text.BM>
              </CircleDiv>
              {t("english")}
            </LanguageMenuItem>
          </LanguageMenu>
        )}
      </div>

      <div
        style={{
          display: "flex",
          backgroundColor: "white",
          height: "100vh",
          width: isMobile ? "100%" : "50%",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            width: isMobile ? "100%" : "auto",
            padding: isMobile ? "0 20px" : "0",
          }}
        >
          <Text.H2 color={theme.color.navy}>{getTitle(type)}</Text.H2>
          <Text.BR color={theme.color.gray90} style={{ marginTop: 10 }}>
            {getSubTitle(type)}
          </Text.BR>
          {type === ScreenType.LOGIN_TENANT && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                marginTop: isMobile && 20,
              }}
            >
              <Label>{t("phone_num")}</Label>
              <PhoneInput
                style={{ direction: isRtl === true ? "ltr" : "ltr" }}
                inputStyle={{
                  direction: isRtl === true ? "ltr" : "ltr",
                  borderRadius: 21,
                  border: `1px solid var(--primary-blue, #514EF3)`,
                  background: `var(--grey-grey-10, #F6FAFD)`,
                  height: 44,
                  width: "100%",
                }}
                containerStyle={{
                  borderRadius: 21,
                  border: "none",
                  background: "FFFFFF",
                }}
                flagButtonStyle={{
                  border: `1px solid var(--primary-blue, #514EF3)`,
                  background: `var(--grey-grey-10, #F6FAFD)`,
                  borderRadius: 21,
                }}
                country={"il"}
                value={phone}
                onChange={handleOnChange}
                onEnterKeyPress={(e) => handelByType(e, type)}
                dir={isRtl === true ? "ltr" : "ltr"}
                inputProps={{
                  name: "phone",
                  required: true,
                  autoFocus: true,
                }}
              />
            </div>
          )}

          {(type === ScreenType.LOGIN_ADMIN ||
            type === ScreenType.FORGOT_PASSWORD_ADMIN) && (
              <InputMUI
                {...email}
                variant="outlined"
                onBlur={handleChange("email")}
                onChange={handleChange("email")}
                label={type === ScreenType.LOGIN_ADMIN ? t("username") : ""}
                placeholder={
                  type === ScreenType.LOGIN_ADMIN
                    ? t("username_placeholder")
                    : t("email_placeholder")
                }
                startImg={userImg}
                disabled={adminPasswordChecked}
              />
            )}
          {type === ScreenType.LOGIN_ADMIN &&
            adminPasswordChecked &&
            adminHasPassword && (
              <div style={{ marginTop: 20, width: "100%" }}>
                <InputMUI
                  {...pwd}
                  variant="outlined"
                  onBlur={handleChange("pwd")}
                  onChange={handleChange("pwd")}
                  type={"password"}
                  startImg={pwdImg}
                />
              </div>
            )}
          {type === ScreenType.LOGIN_ADMIN &&
            adminPasswordChecked &&
            !adminHasPassword && (
              <>
                <div style={{ marginTop: 20, width: "100%" }}>
                  <InputMUI
                    {...pwd}
                    variant="outlined"
                    onBlur={handleChange("pwd")}
                    onChange={handleChange("pwd")}
                    type={"password"}
                    startImg={pwdImg}
                    label={t("new_pwd")}
                    placeholder={t("new_pwd_placeholder")}
                  />
                </div>
                <div style={{ marginTop: 20, width: "100%" }}>
                  <InputMUI
                    placeholder={t("confirmation_pwd_placeholder")}
                    label={t("confirmation_pwd")}
                    variant="outlined"
                    startImg={pwdImg}
                    {...newPwd}
                    onBlur={handleChange("newPwd")}
                    onChange={handleChange("newPwd")}
                    type={"password"}
                  />
                </div>
              </>
            )}
          {type === ScreenType.NEW_PASSWORD_ADMIN && (
            <InputMUI
              style={{ borderRadius: 21 }}
              {...pwd}
              variant="outlined"
              onBlur={handleChange("pwd")}
              onChange={handleChange("pwd")}
              type={"password"}
              startImg={pwdImg}
            ></InputMUI>
          )}

          {type === ScreenType.NEW_PASSWORD_ADMIN && (
            <InputMUI
              placeholder={t("newPwd")}
              label={t("new_pwd")}
              variant="outlined"
              startImg={pwdImg}
              {...newPwd}
              onBlur={handleChange("newPwd")}
              onChange={handleChange("newPwd")}
              type={"password"}
              style={{ textAlign: "center" }}
            />
          )}

          {type === ScreenType.LOGIN_ADMIN && (
            <Text.SM
              color={theme.color.primary}
              style={{
                textAlign: "left",
                marginTop: 10,
                cursor: "pointer",
                marginLeft: "0.5em",
                marginRight: "0.5em",
              }}
              onClick={() => navigate("/forgot_password_admin")}
            >
              {t("forgot_password")}
            </Text.SM>
          )}
          <Button ref={submitButtonRef} type="submit" style={{ marginTop: 50 }}>
            {isLoading && (
              <div>
                <ClipLoader color={theme.color.white} />
              </div>
            )}
            {type === ScreenType.LOGIN_ADMIN
              ? adminPasswordChecked
                ? adminHasPassword
                  ? t("login")
                  : t("create_password")
                : t("continue")
              : type === ScreenType.LOGIN_TENANT
                ? t("request_opt")
                : t("send")}
          </Button>
          {messageNotification.show && (
            <MessageNotification
              type={messageNotification.type || MessageType.ERROR}
              text={messageNotification.text}
              onClose={() => {
                setMessageNotification({
                  show: false,
                });
                userStore.setError("");
              }}
              isLogin={true}
              buttonPosition={messageNotification.buttonPosition}
            />
          )}
        </div>
      </div>
    </form>
  );
};

export default inject("userStore")(observer(LoginAdmin));
export { ScreenType };
